// Die oeffentliche Abstimmungs-Seite `/termin/<token>` — das EINZIGE Stueck
// Atlas, das je ein Externer zu sehen bekommt.
//
// Warum sie ausserhalb von allem anderen steht:
//
//   • **Kein Login, kein Rahmen.** Die Route haengt in `App.tsx` neben `/login`,
//     NICHT unter `RequireAuth`/`Layout`. Ein Kunde hat keinen Bearer-Token; jede
//     Navigation, Seitenleiste oder Login-Maske waere fuer ihn eine Sackgasse.
//   • **Kein `api`-Client.** Der (`src/api.ts`) haengt den Bearer-Token aus dem
//     localStorage an JEDEN Aufruf. Diese Seite muss sich fuer einen Fremden
//     GENAUSO verhalten wie fuer einen angemeldeten Kollegen, der denselben Link
//     anklickt — also darf ihr Verhalten gar nicht erst an einer Sitzung haengen.
//     Deshalb ein eigener, winziger `fetch` ohne Authorization-Header.
//   • **Erwartete Ausgaenge sind Zustaende, keine Ausnahmen.** 404 (toter Link)
//     und 409 (der Raum ist enger geworden) sind der Normalbetrieb dieser Seite.
//     Sie kommen als Wert zurueck, nicht als `throw` — ein Fehlerdialog waere
//     genau die Sackgasse, die es hier nicht geben darf.
//
// Die Adressen, und warum sie auseinanderfallen:
//   • `/termin/<token>`        — DIESE Seite. Steht so in der Mail beim Kunden.
//   • `/calendar/vote/<token>` — die API dahinter (app/routers/calendar_polls.py).
// Die API liegt unter `/calendar`, weil die Edge-Kante vor der Prod-URL eine
// Praefix-Whitelist fuehrt: `/calendar` erreicht das Backend nachweislich,
// `/poll` NICHT (dort kommt der SPA-Fallback zurueck, HTTP 200 + index.html).
// Die Begruendung im Volltext steht am `public_router`. Wer hier auf ein
// huebscheres Praefix umstellt, legt die Seite in Prod still — die
// Stoerungs-Behandlung unten faengt das ab, aber die Funktion waere tot.
//
// Die Verengung ist der Kern: `GET /calendar/vote/<token>` liefert nur die Termine, die
// nach den bisherigen Zusagen ueberhaupt noch moeglich sind
// (`app/services/calendar_polls.py::feasible_slot_ids`). Wer als zweiter oeffnet,
// sieht weniger als in seiner Mail stand. Das ist Absicht — aber es muss
// DASTEHEN, sonst liest es sich, als haette man ihm etwas weggenommen.
//
// Sprache: Deutsch, Sie-Form, geschaeftlich freundlich. Interne Woerter („Slot",
// „Poll", „Token", „mandatory", „required") haben auf dieser Seite NICHTS zu
// suchen — auch nicht in sichtbaren Fehlermeldungen.
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CalendarCheck2, Check, Loader2 } from "lucide-react";
import { Button } from "../ui";

// ── Vertrag mit dem Server ───────────────────────────────────────────────────
// Form aus `app/schemas/calendar_poll.py::PollPublicView`. Bewusst NUR die
// Felder, die dort stehen: das `response_model` liefert keine anderen
// Teilnehmer und keinen Organisator, und diese Seite fragt auch nicht danach.

interface PollSlot {
  id: string;
  starts_at: string;
  ends_at: string;
}

interface PollView {
  subject: string;
  description: string | null;
  duration_minutes: number;
  status: string;
  expires_at: string;
  participant_name: string | null;
  participant_email: string;
  participant_role: string;
  responded_at: string | null;
  /** Die NOCH MOEGLICHEN Zeitfenster — nicht die urspruenglich vorgeschlagenen. */
  slots: PollSlot[];
  selected_slot_ids: string[];
  booked_slot: PollSlot | null;
  /** Deutscher Klartext des Servers fuer die Zustaende, in denen nichts mehr
   *  passiert. Wird uebernommen statt nachgebaut — zwei Formulierungen
   *  derselben Lage laufen frueher oder spaeter auseinander. */
  message: string | null;
}

/** Ergebnis eines Aufrufs. `dead` ist der EINE Ausgang fuer „unbekannt" UND
 *  „abgelaufen": der Server macht sie bewusst ununterscheidbar (identische 404,
 *  `_DEAD_LINK`), damit sich Links nicht durchprobieren lassen. Es gibt hier
 *  deshalb keinen zweiten Zweig — die Ununterscheidbarkeit ist strukturell und
 *  nicht davon abhaengig, dass jemand zwei Texte gleich haelt. */
type Loaded =
  | { kind: "view"; view: PollView }
  | { kind: "dead" }
  | { kind: "offline" };

/** Zusaetzliche Ausgaenge der Stimmabgabe. `narrowed` = 409, waehrend der Kunde
 *  ueberlegt hat, haben andere zugesagt. `closed` = 409, die Abstimmung ist
 *  inzwischen entschieden. Beide fuehren zum Neuladen, nicht in einen Dialog. */
type Voted = Loaded | { kind: "narrowed" } | { kind: "closed" };

/** Sieht der Rumpf wie eine Abstimmungs-Ansicht aus?
 *
 *  Der Riegel gegen einen stillen Totalausfall: fehlt `/poll` in `API_PATHS`
 *  (vite.config.ts) oder faellt die Edge-Kante zurueck, antwortet der
 *  SPA-Fallback mit HTTP 200 und `index.html`. Ohne diese Pruefung landete das
 *  als „erfolgreich geladen" in der Seite und ergaebe eine Ansicht ohne Inhalt.
 *  Lieber ein benannter Stoerungs-Zustand als eine leere Seite. */
function looksLikeView(body: unknown): body is PollView {
  const v = body as Partial<PollView> | null;
  return (
    !!v &&
    typeof v === "object" &&
    typeof v.subject === "string" &&
    typeof v.status === "string" &&
    Array.isArray(v.slots)
  );
}

async function call(path: string, init?: RequestInit): Promise<Voted> {
  let res: Response;
  try {
    res = await fetch(path, {
      ...init,
      headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
    });
  } catch {
    // Kein Netz, DNS weg, Server aus. Das ist KEINE Aussage ueber den Link —
    // deshalb darf es auch nicht wie ein toter Link aussehen.
    return { kind: "offline" };
  }
  if (res.status === 404) return { kind: "dead" };
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }
  if (res.status === 409) {
    // Der Server unterscheidet zwei Konflikte. Fuer den Besucher zaehlt nur,
    // welchen Satz er liest — der Zustand danach ist in beiden Faellen „Seite
    // frisch laden und schauen, wo wir stehen".
    const detail = (body as { detail?: { error?: string } } | null)?.detail;
    return detail?.error === "poll_not_open"
      ? { kind: "closed" }
      : { kind: "narrowed" };
  }
  if (!res.ok || !looksLikeView(body)) return { kind: "offline" };
  return { kind: "view", view: body };
}

// ── Deutsche Zeitangaben ─────────────────────────────────────────────────────
// Feste Zeitzone Europe/Berlin und festes Locale „de-DE" — exakt wie
// `app/mail/calendar_polls.py::format_slot`. Der Kunde legt Mail und Seite
// nebeneinander; zwei verschiedene Stunden waeren der teuerste Fehler hier.
// (Beides explizit gesetzt: die Anzeige darf weder an der Zeitzone des Geraets
// noch an der Sprache des Browsers haengen.)
const TZ = "Europe/Berlin";
const DATE_FMT = new Intl.DateTimeFormat("de-DE", {
  timeZone: TZ,
  weekday: "long",
  day: "numeric",
  month: "long",
  year: "numeric",
});
const TIME_FMT = new Intl.DateTimeFormat("de-DE", {
  timeZone: TZ,
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});
/** Tages-Kennung in Berliner Zeit — fuer den Vergleich „endet am selben Tag?".
 *  `Date.getDate()` waere die Zeitzone des GERAETS und laege bei einem Kunden
 *  mit Reise-Laptop daneben. */
const DAY_FMT = new Intl.DateTimeFormat("en-CA", {
  timeZone: TZ,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

const dateDe = (iso: string) => DATE_FMT.format(new Date(iso));
const timeDe = (iso: string) => TIME_FMT.format(new Date(iso));

/** „Montag, 3. August 2026" + „09:00–09:30 Uhr". Getrennt, weil die Seite die
 *  beiden Zeilen untereinander setzt — auf 390 px passt die Langform nicht in
 *  eine Zeile, und ein Umbruch mitten im Datum liest sich wie ein Fehler. */
function slotLines(slot: PollSlot): { tag: string; zeit: string } {
  const sameDay =
    DAY_FMT.format(new Date(slot.starts_at)) ===
    DAY_FMT.format(new Date(slot.ends_at));
  return {
    tag: dateDe(slot.starts_at),
    // Laeuft der Termin ueber Mitternacht, steht das Enddatum dabei: „21:00–
    // 05:00 Uhr" allein waere eine falsche Auskunft (dieselbe Regel wie in der Mail).
    zeit: sameDay
      ? `${timeDe(slot.starts_at)}–${timeDe(slot.ends_at)} Uhr`
      : `${timeDe(slot.starts_at)} Uhr – ${dateDe(slot.ends_at)}, ${timeDe(slot.ends_at)} Uhr`,
  };
}

/** „30 Minuten", „1 Stunde", „1,5 Stunden". Ein Kunde liest keine „90 Minuten",
 *  wenn „1,5 Stunden" gemeint ist. */
function dauerDe(minutes: number): string {
  if (minutes < 60) return `${minutes} Minuten`;
  const h = minutes / 60;
  const txt = Number.isInteger(h) ? String(h) : h.toFixed(1).replace(".", ",");
  return h === 1 ? "1 Stunde" : `${txt} Stunden`;
}

/** Die eigene Rolle in Worten. „required"/„optional" sind interne Marken und
 *  duerfen auf dieser Seite nicht auftauchen. */
function rolleDe(role: string): string {
  return role === "optional"
    ? "Sie sind zusätzlich eingeladen — Ihre Rückmeldung hilft, der Termin wird aber auch ohne sie gefunden."
    : "Ihre Rückmeldung wird für die Terminfindung gebraucht.";
}

/** Was nach einem 409 dasteht. Haengt an ZWEI Dingen, nicht an einem: welcher
 *  Konflikt es war UND ob nach dem Neuladen ueberhaupt noch etwas zu waehlen
 *  ist. „Bitte waehlen Sie noch einmal" unter einer Ansicht ohne Auswahl waere
 *  eine Sackgasse mit freundlichem Text — also die schlimmere Sorte.
 *  Kein „409", kein „Konflikt", kein „Fehler": der Kunde hat nichts falsch
 *  gemacht, es ist ihm nur jemand zuvorgekommen. */
function konfliktText(kind: "narrowed" | "closed", offen: boolean): string {
  if (kind === "closed") {
    return "Ihre Auswahl kam einen Moment zu spät — die Terminabstimmung war da bereits abgeschlossen. Hier ist der aktuelle Stand.";
  }
  return offen
    ? "Ihre Auswahl konnten wir leider nicht mehr übernehmen: Während Sie die Seite offen hatten, haben andere Teilnehmer zugesagt und damit die möglichen Zeiten eingegrenzt. Unten stehen die Zeiten, die jetzt noch für alle passen — bitte wählen Sie daraus noch einmal aus."
    : "Ihre Auswahl konnten wir nicht mehr übernehmen: Während Sie die Seite offen hatten, haben andere Teilnehmer zugesagt. Inzwischen steht das Ergebnis fest — hier ist es.";
}

// ── Bausteine ────────────────────────────────────────────────────────────────

/** Der ruhige Rahmen: eine Karte auf leerer Flaeche, mobil zuerst.
 *  `min-h-full` statt `h-full`, damit lange Terminlisten den Dokument-Scroll
 *  bekommen statt aus einer 100%-Box zu laufen. */
function Seite({
  state,
  email,
  children,
}: {
  state: string;
  /** Die Adresse des Empfaengers, wo sie bekannt ist. Sie steht im FUSS und
   *  nicht oben: sie beantwortet „bin ich hier richtig?", und diese Frage
   *  stellt sich erst, wenn oben schon klar ist, worum es geht. Vier
   *  Grau-Absaetze vor dem ersten antippbaren Termin kosten mehr, als die
   *  Zeile oben einbringt. */
  email?: string;
  children: React.ReactNode;
}) {
  return (
    <div
      data-termin-state={state}
      className="min-h-full bg-surface-2 px-4 py-8 sm:px-6 sm:py-12"
    >
      <div className="mx-auto w-full max-w-xl">
        {/* Absender-Kennung. Der Kunde soll auf einen Blick sehen, bei wem er
            gelandet ist. Bewusst das HAUS und keine Person: die oeffentliche
            Antwort traegt keinen Organisator (app/schemas/calendar_poll.py —
            „ein externer Kunde darf nicht den Verteiler des Hauses sehen"), und
            die Person kennt er ohnehin, die Einladung kam von ihrer Adresse. */}
        <div className="mb-6 flex items-center gap-2.5">
          <div className="grid h-9 w-9 place-items-center rounded-xl bg-accent-600 font-semibold text-white">
            b
          </div>
          <div className="leading-tight">
            <div className="text-sm font-semibold text-text">bitop AG</div>
            <div className="text-xs text-text-2">Terminabstimmung</div>
          </div>
        </div>
        <div className="rounded-2xl border border-border bg-surface p-5 shadow-sm sm:p-8">
          {children}
        </div>
        <p data-termin-self="1" className="mt-6 text-center text-xs leading-relaxed text-text-3">
          {email && <>Diese Anfrage ging an {email}. </>}
          Der Link gilt persönlich für Sie — bitte leiten Sie ihn nicht weiter.
        </p>
      </div>
    </div>
  );
}

/** Eine waehlbare Zeit. Ein echtes `<button>` mit `aria-pressed` — kein `div`
 *  mit Klick-Handler: die Seite bekommen Leute vor die Nase, die wir nicht
 *  kennen, und ein Teil von ihnen bedient sie mit der Tastatur oder einem
 *  Screenreader. Mindesthoehe ueber `py-3.5` + zwei Zeilen ≈ 64 px, also klar
 *  ueber der Daumen-Grenze von 44 px. */
function ZeitKnopf({
  slot,
  gewaehlt,
  onToggle,
}: {
  slot: PollSlot;
  gewaehlt: boolean;
  onToggle: () => void;
}) {
  const { tag, zeit } = slotLines(slot);
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={gewaehlt}
      data-termin-slot={slot.id}
      data-termin-slot-chosen={gewaehlt ? "1" : "0"}
      className={[
        "flex w-full items-center gap-3 rounded-xl border px-4 py-3.5 text-left transition-colors",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-500/60",
        gewaehlt
          ? "border-accent-500 bg-accent-50"
          : "border-border bg-surface hover:bg-surface-3",
      ].join(" ")}
    >
      <span
        aria-hidden="true"
        data-termin-check={gewaehlt ? "1" : "0"}
        className={[
          "grid h-6 w-6 shrink-0 place-items-center rounded-md border",
          gewaehlt
            // `dark:text-ink-900` aus demselben Grund wie am Hauptknopf: im
            // Dunkelmodus ist `accent-600` die HELLE Stufe, weiss darauf kommt
            // auf 2.1:1. Der Haken ist zwar doppelt kodiert (Rahmen + Flaeche
            // zeigen die Wahl auch), aber er ist das Zeichen, auf das man schaut.
            ? "border-accent-600 bg-accent-600 text-white dark:text-ink-900"
            : "border-border-strong bg-surface",
        ].join(" ")}
      >
        {gewaehlt && <Check className="h-4 w-4" strokeWidth={3} />}
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-medium text-text">{tag}</span>
        <span className="block text-sm text-text-2">{zeit}</span>
      </span>
    </button>
  );
}

/** Termin ohne Auswahl — fuer die abgeschlossenen Zustaende (gebucht, kein
 *  gemeinsamer Termin). Dieselbe Optik, damit die Seite nicht die Form wechselt,
 *  nur weil nichts mehr zu tun ist. */
function ZeitZeile({ slot, stark }: { slot: PollSlot; stark?: boolean }) {
  const { tag, zeit } = slotLines(slot);
  return (
    <div
      data-termin-slot-static={slot.id}
      className={[
        "rounded-xl border px-4 py-3.5",
        stark ? "border-accent-500 bg-accent-50" : "border-border bg-surface-2",
      ].join(" ")}
    >
      <div className="text-sm font-medium text-text">{tag}</div>
      <div className="text-sm text-text-2">{zeit}</div>
    </div>
  );
}

function Hinweis({
  ton = "neutral",
  hook,
  children,
}: {
  ton?: "neutral" | "warnung";
  hook?: string;
  children: React.ReactNode;
}) {
  return (
    <p
      {...(hook ? { [hook]: "1" } : {})}
      className={[
        "rounded-xl px-4 py-3 text-sm leading-relaxed",
        ton === "warnung"
          ? "bg-warning-soft text-text"
          : "bg-surface-2 text-text-2",
      ].join(" ")}
    >
      {children}
    </p>
  );
}

// ── Die Seite ────────────────────────────────────────────────────────────────

export default function Termin() {
  const { token = "" } = useParams();
  const [state, setState] = useState<Loaded | { kind: "loading" }>({
    kind: "loading",
  });
  const [gewaehlt, setGewaehlt] = useState<string[]>([]);
  /** Erklaerung zum letzten 409. Steht NEBEN der frisch geladenen Auswahl, nicht
   *  statt ihrer — der Kunde soll weiterklicken koennen, nicht wegklicken. */
  const [konflikt, setKonflikt] = useState<"narrowed" | "closed" | null>(null);
  const [sendet, setSendet] = useState(false);
  /** Zwischenschritt fuer „mir passt keiner". Inline, nie `window.confirm`
   *  (Projekt-Regel) — und noetig, weil diese Antwort fuer einen Teilnehmer,
   *  dessen Zusage gebraucht wird, die Abstimmung SOFORT beendet
   *  (`feasible_slot_ids` schneidet mit der leeren Menge → `no_common_slot`).
   *  Ein Fehlklick waere fuer ihn nicht mehr zurueckzunehmen. */
  const [fragtAbsage, setFragtAbsage] = useState(false);

  // Der Tab-Titel ist das Erste, was der Kunde sieht. „bitop Atlas" ist ein
  // interner Produktname und hat vor einem Externen nichts verloren.
  useEffect(() => {
    document.title = "Terminabstimmung – bitop AG";
  }, []);

  /** Antwort uebernehmen. Die eigene Auswahl wird IMMER aus dem Server-Stand
   *  gesetzt: `selected_slot_ids` ist bereits auf die noch moeglichen Zeiten
   *  beschnitten, und eine lokal weitergefuehrte Auswahl waere genau die
   *  veraltete Sicht, gegen die der Server mit 409 schuetzt. */
  const uebernehmen = useCallback((r: Loaded) => {
    setState(r);
    if (r.kind === "view") setGewaehlt(r.view.selected_slot_ids);
  }, []);

  const laden = useCallback(async () => {
    setState({ kind: "loading" });
    const r = await call(`/calendar/vote/${encodeURIComponent(token)}`);
    // Ein GET kann die 409-Ausgaenge nicht liefern; die Enge des Typs hier
    // waere Kosmetik, der Ruecksprung auf „Stoerung" ist die ehrliche Antwort.
    uebernehmen(r.kind === "view" || r.kind === "dead" ? r : { kind: "offline" });
  }, [token, uebernehmen]);

  useEffect(() => {
    void laden();
  }, [laden]);

  async function absenden(ids: string[]) {
    setSendet(true);
    setFragtAbsage(false);
    const r = await call(`/calendar/vote/${encodeURIComponent(token)}/vote`, {
      method: "POST",
      body: JSON.stringify({ slot_ids: ids }),
    });
    if (r.kind === "narrowed" || r.kind === "closed") {
      // DER interessante Fall. Nicht die Antwort des Servers auseinandernehmen,
      // sondern frisch laden: `available` traegt nur Kennungen, keine Zeiten —
      // und genau das rät die Server-Meldung auch. Ein Weg statt zwei.
      setKonflikt(r.kind);
      const frisch = await call(`/calendar/vote/${encodeURIComponent(token)}`);
      uebernehmen(
        frisch.kind === "view" || frisch.kind === "dead"
          ? frisch
          : { kind: "offline" }
      );
      setSendet(false);
      return;
    }
    setKonflikt(null);
    uebernehmen(r.kind === "view" || r.kind === "dead" ? r : { kind: "offline" });
    setSendet(false);
  }

  if (state.kind === "loading") {
    return (
      <Seite state="loading">
        <div className="flex items-center gap-3 py-6 text-sm text-text-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          Einen Moment, die Terminvorschläge werden geladen …
        </div>
      </Seite>
    );
  }

  // Server nicht erreichbar (oder eine Antwort, die keine Abstimmung ist).
  // Ausdruecklich KEINE Aussage ueber den Link — die waere hier gelogen.
  if (state.kind === "offline") {
    return (
      <Seite state="offline">
        <h1 className="text-xl font-semibold text-text">
          Die Seite ist gerade nicht erreichbar
        </h1>
        <p className="mt-3 text-sm leading-relaxed text-text-2">
          Ihre Terminvorschläge konnten nicht geladen werden. Das liegt nicht an
          Ihrem Link. Bitte versuchen Sie es in ein paar Minuten noch einmal.
        </p>
        <Button
          variant="secondary"
          size="lg"
          className="mt-5 w-full sm:w-auto"
          data-termin-retry="1"
          onClick={() => void laden()}
        >
          Erneut versuchen
        </Button>
      </Seite>
    );
  }

  // Toter Link. EIN Text fuer „unbekannt" UND „abgelaufen" — der Server liefert
  // fuer beides dieselbe 404, damit sich Links nicht durchprobieren lassen.
  // Dieser Zweig rendert FESTEN Text und nie den Rumpf der Antwort: sonst
  // haengt die Ununterscheidbarkeit daran, dass zwei Server-Meldungen fuer alle
  // Zeit gleich bleiben.
  if (state.kind === "dead") {
    return (
      <Seite state="dead">
        <h1 className="text-xl font-semibold text-text">
          Dieser Link ist nicht mehr gültig
        </h1>
        <p className="mt-3 text-sm leading-relaxed text-text-2">
          Über diesen Link kann derzeit nicht abgestimmt werden. Möglicherweise
          ist die Terminabstimmung bereits abgeschlossen. Bitte antworten Sie
          einfach auf unsere E-Mail — wir melden uns dann bei Ihnen.
        </p>
      </Seite>
    );
  }

  const v = state.view;
  const offen = v.status === "open";
  const hatGeantwortet = v.responded_at !== null;
  const anrede = v.participant_name ? `Guten Tag ${v.participant_name},` : "Guten Tag,";

  // ── Abgeschlossene Zustaende: gebucht / in Buchung / gescheitert / kein
  // gemeinsamer Termin. Keiner davon darf als leere Seite enden, deshalb hat der
  // `else`-Zweig unten einen Auffang-Satz statt einer Annahme ueber die Liste
  // der Zustaende. Der Klartext kommt vom Server (`public_message`) — er kennt
  // den Zustand, und zwei Formulierungen derselben Lage laufen auseinander.
  if (!offen) {
    const gebucht = v.status === "booked" || v.status === "booking";
    return (
      <Seite state={gebucht ? "booked" : "closed"} email={v.participant_email}>
        <div className="flex items-start gap-3">
          {gebucht && (
            <CalendarCheck2 className="mt-0.5 h-5 w-5 shrink-0 text-accent-600" />
          )}
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-text">
              {gebucht ? "Der Termin steht" : "Diese Terminabstimmung ist beendet"}
            </h1>
            <p className="mt-1 text-sm text-text-2">{v.subject}</p>
          </div>
        </div>

        {/* Auch hier: wer gerade auf „absenden" gedrueckt hat, muss erfahren, was
            aus seinem Klick geworden ist. Ohne diese Zeile springt die Seite
            wortlos auf ein Ergebnis und der Kunde weiss nicht, ob seine Auswahl
            noch gezaehlt wurde. */}
        {konflikt && (
          <div className="mt-5">
            <Hinweis ton="warnung" hook="data-termin-conflict">
              {konfliktText(konflikt, false)}
            </Hinweis>
          </div>
        )}

        {v.booked_slot && (
          <div className="mt-5">
            <ZeitZeile slot={v.booked_slot} stark />
            <p className="mt-2 text-sm text-text-2">
              Dauer: {dauerDe(v.duration_minutes)}
            </p>
          </div>
        )}

        <p
          data-termin-message="1"
          className="mt-5 text-sm leading-relaxed text-text-2"
        >
          {v.message ??
            "Über diesen Link kann nicht mehr abgestimmt werden. Der Organisator meldet sich bei Ihnen."}
        </p>

        {/* Nur das, was der Server-Satz NICHT schon sagt. Er nennt den Versand
            der Einladung; doppelt gesagt liest es sich, als stuenden da zwei
            verschiedene Auskuenfte. Was fehlt, ist die Entlastung: der Kunde
            soll wissen, dass er diese Seite jetzt zumachen kann. */}
        {v.status === "booked" && (
          <p data-termin-done="1" className="mt-3 text-sm leading-relaxed text-text-2">
            Sie müssen hier nichts mehr tun.
          </p>
        )}

        {/* Bei „kein gemeinsamer Termin" liefert der Server die VOLLE Liste der
            Vorschlaege statt des (leeren) Moeglichkeitsraums. Sie zu zeigen ist
            die freundlichere Auskunft: der Kunde sieht, worueber abgestimmt
            wurde, statt vor einer leeren Karte zu stehen. */}
        {!gebucht && v.slots.length > 0 && (
          <div className="mt-5">
            <div className="mb-2 text-sm font-medium text-text">
              Zur Abstimmung standen:
            </div>
            <div className="space-y-2">
              {v.slots.map((s) => (
                <ZeitZeile key={s.id} slot={s} />
              ))}
            </div>
          </div>
        )}
      </Seite>
    );
  }

  // ── Offen: auswaehlen und abschicken ───────────────────────────────────────
  const toggle = (id: string) =>
    setGewaehlt((cur) =>
      cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]
    );
  const geaendert =
    gewaehlt.length !== v.selected_slot_ids.length ||
    gewaehlt.some((id) => !v.selected_slot_ids.includes(id));

  return (
    <Seite state={hatGeantwortet ? "answered" : "choose"} email={v.participant_email}>
      <p data-termin-greeting="1" className="text-sm text-text-2">
        {anrede}
      </p>
      <h1 className="mt-1 text-xl font-semibold leading-snug text-text sm:text-2xl">
        {v.subject}
      </h1>
      {/* Die Aufforderung steht GANZ oben und in einem Satz. Wer die Seite aus
          der Mail heraus auf dem Telefon oeffnet, soll in fuenf Sekunden wissen,
          was von ihm gewollt ist — nicht erst nach vier Absaetzen Kleingedrucktem. */}
      <p className="mt-3 text-base leading-relaxed text-text">
        Bitte wählen Sie alle Zeiten aus, zu denen Sie können.
      </p>

      {/* Dauer und Frist in EINER Zeile. Als zweizeilige Beschreibungsliste
          standen hier auf 390 px vier Zeilen Grau zwischen der Aufforderung und
          dem ersten antippbaren Termin. */}
      <p className="mt-2 text-sm text-text-2">
        <span data-termin-duration="1">{dauerDe(v.duration_minutes)}</span>
        {" · Rückmeldung bis "}
        {dateDe(v.expires_at)}, {timeDe(v.expires_at)} Uhr
      </p>

      {v.description && (
        <p className="mt-4 whitespace-pre-line text-sm leading-relaxed text-text-2">
          {v.description}
        </p>
      )}

      <p data-termin-role="1" className="mt-3 text-sm leading-relaxed text-text-2">
        {rolleDe(v.participant_role)}
      </p>

      {/* Der 409-Fall: waehrend der Kunde ueberlegt hat, haben andere zugesagt.
          Erklaert ohne Fachsprache, mit dem Grund — und die frisch geladene
          Auswahl steht direkt darunter, damit es weitergeht statt aufzuhoeren. */}
      {konflikt && (
        <div className="mt-5">
          <Hinweis ton="warnung" hook="data-termin-conflict">
            {konfliktText(konflikt, true)}
          </Hinweis>
        </div>
      )}

      {hatGeantwortet && !konflikt && (
        <div className="mt-5">
          <Hinweis hook="data-termin-thanks">
            {v.selected_slot_ids.length > 0
              ? "Vielen Dank, Ihre Rückmeldung ist bei uns eingegangen. Sie können Ihre Auswahl hier jederzeit ändern, solange die Abstimmung läuft."
              : "Sie haben uns zurückgemeldet, dass Ihnen keine der Zeiten passt. Falls sich das ändert, können Sie hier noch etwas auswählen."}
          </Hinweis>
        </div>
      )}

      <div className="mt-6 space-y-2">
        {v.slots.map((s) => (
          <ZeitKnopf
            key={s.id}
            slot={s}
            gewaehlt={gewaehlt.includes(s.id)}
            onToggle={() => toggle(s.id)}
          />
        ))}
      </div>

      {/* Die Verengung, unbedingt formuliert. Der Server liefert nur den
          Moeglichkeitsraum, nicht die urspruengliche Liste — die Seite KANN also
          gar nicht erkennen, ob eingegrenzt wurde. Also steht der Satz immer da
          und ist immer wahr: er erklaert die Luecke fuer den, der sie bemerkt,
          ohne dem anderen etwas Falsches zu erzaehlen. Ohne ihn liest sich eine
          kuerzere Liste als „man hat mir etwas weggenommen".
          NICHT gleichzeitig mit dem Konflikt-Kasten: der sagt dasselbe, nur
          konkret und auf den eben passierten Fall bezogen. Zweimal dieselbe
          Erklaerung auf einer Seite laesst den Leser nach dem Unterschied
          suchen, den es nicht gibt. */}
      {!konflikt && (
        <p
          data-termin-narrow-hint="1"
          className="mt-3 text-xs leading-relaxed text-text-3"
        >
          Hier stehen die Zeiten, die aktuell noch für alle Beteiligten möglich
          sind. Falls das weniger sind als in unserer E-Mail: Dann haben andere
          Teilnehmer bereits zugesagt, und die Auswahl hat sich dadurch
          eingegrenzt.
        </p>
      )}

      <div className="mt-6 border-t border-border pt-5">
        {/* Zwei Abweichungen vom geteilten `Button`, beide gemessen:
            • `min-h-[3rem]` — `size="lg"` liefert 36 px. Das reicht am
              Schreibtisch, aber diese Seite wird ueberwiegend mit dem Daumen
              bedient; unter 44 px trifft man daneben.
            • `dark:text-ink-900` — die Accent-Rampe DREHT im Dunkelmodus
              (themes.css: `--accent-500` ist dort das HELLE Petrol, 74/165/181).
              Die weisse Schrift des Primitives kommt damit auf 2.86:1, also weit
              unter der Lesbarkeitsgrenze. Dunkle Schrift auf der hellen Flaeche
              ergibt 6.3:1 — dieselbe Flaeche, richtig herum gepaart.
            Bewusst HIER und nicht in `src/ui/button.tsx`: eine Aenderung am
            Primitive wirkt atlas-weit und gehoert nicht in diese Task. */}
        <Button
          variant="primary"
          size="lg"
          className="min-h-[3rem] w-full dark:text-ink-900"
          data-termin-submit="1"
          loading={sendet}
          disabled={gewaehlt.length === 0 || (hatGeantwortet && !geaendert)}
          onClick={() => void absenden(gewaehlt)}
        >
          {hatGeantwortet ? "Auswahl ändern" : "Auswahl absenden"}
        </Button>
        <p className="mt-2 text-center text-xs text-text-3">
          {gewaehlt.length === 0
            ? "Noch keine Zeit ausgewählt"
            : `${gewaehlt.length} von ${v.slots.length} Zeiten ausgewählt`}
        </p>

        {/* Ohne diesen Weg koennte ein Kunde, dem nichts passt, nur schweigen —
            und der Organisator wartete ins Leere. Serverseitig ist das eine
            leere Auswahl, also eine gueltige Antwort. */}
        {!fragtAbsage ? (
          <button
            type="button"
            data-termin-none="1"
            disabled={sendet}
            onClick={() => setFragtAbsage(true)}
            className="mx-auto mt-2 flex min-h-[2.75rem] items-center justify-center rounded-md px-3 text-sm text-text-2 underline underline-offset-4 hover:text-text focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-500/60 disabled:opacity-40"
          >
            Mir passt keiner dieser Termine
          </button>
        ) : (
          <div
            data-termin-none-confirm="1"
            className="mt-4 rounded-xl border border-border bg-surface-2 p-4"
          >
            <p className="text-sm leading-relaxed text-text">
              Wir melden dem Organisator, dass Ihnen keiner der vorgeschlagenen
              Termine passt. Er meldet sich dann mit neuen Vorschlägen bei Ihnen.
            </p>
            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              <Button
                variant="secondary"
                size="lg"
                className="min-h-[3rem] w-full sm:w-auto"
                data-termin-none-yes="1"
                loading={sendet}
                onClick={() => void absenden([])}
              >
                Ja, keiner passt
              </Button>
              <Button
                variant="ghost"
                size="lg"
                className="min-h-[3rem] w-full sm:w-auto"
                data-termin-none-cancel="1"
                disabled={sendet}
                onClick={() => setFragtAbsage(false)}
              >
                Zurück zur Auswahl
              </Button>
            </div>
          </div>
        )}
      </div>
    </Seite>
  );
}
